import { useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Icon from "@Components/ui/icon/Icon";
import useWindowDimensions from "../../../../hooks/useWindowDimensions";
import RecipeCard from "./RecipeCard";
import {
  RecipeSliderProps,
  IArrowProps,
  LazyType,
  IRefProps,
  ISliderItem,
} from "./RecipeSlider.types";
import classes from "./RecipeSlider.module.scss";

export default function RecipeSlider({ recipeSliderItems }: RecipeSliderProps) {
  const items = recipeSliderItems;
  const { width } = useWindowDimensions();
  const maxSlides = width > 480 ? 2 : 1;
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef<IRefProps>(null);

  const SamplePrevArrow = (props: IArrowProps) => {
    const { currentSlide } = props;
    const disabledArrow = currentSlide !== undefined && currentSlide < 1;
    const arrowClass = disabledArrow
      ? `${
          classes["arrow"] +
          " " +
          classes["arrow-left"] +
          " " +
          classes["disabled-arrow"]
        }`
      : `${classes["arrow"] + " " + classes["arrow-left"]}`;

    const handleLeftArrowClick = () => {
      if (currentSlide && currentSlide > 0) setCurrentSlide(currentSlide - 1);
    };

    return (
      <div
        data-testid="arrow_left"
        className={arrowClass}
        onClick={handleLeftArrowClick}
      >
        <Icon iconName="arrow-left-thin" />
      </div>
    );
  };

  const SampleNextArrow = (props: IArrowProps) => {
    const { slideCount, currentSlide } = props;
    const disabledArrow =
      currentSlide !== undefined &&
      slideCount !== undefined &&
      currentSlide >= slideCount - maxSlides;
    const arrowClass = disabledArrow
      ? `${
          classes["arrow"] +
          " " +
          classes["arrow-right"] +
          " " +
          classes["disabled-arrow"]
        }`
      : `${classes["arrow"] + " " + classes["arrow-right"]}`;

    const handleRightArrowClick = () => {
      if (slideCount && currentSlide !== undefined && currentSlide < slideCount)
        setCurrentSlide(currentSlide + 1);
    };

    return (
      <div
        data-testid="arrow_right"
        className={arrowClass}
        onClick={handleRightArrowClick}
      >
        <Icon iconName="arrow-right-thin" />
      </div>
    );
  };

  // Carousel/Slider settings:
  const settings = {
    infinite: false,
    lazyLoad: "progressive" as LazyType,
    swipeDirection: "down",
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 1,
    arrows: true,
    adaptiveHeight: false,
    prevArrow: <SamplePrevArrow />,
    nextArrow: <SampleNextArrow />,
    dots: false,
    className: classes["section-slider"],
    initialSlide: currentSlide,
    beforeChange: (prev: number, next: number) => {
      if (next !== prev) setCurrentSlide(next);
    },
    responsive: [
      {
        breakpoint: 960,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
          infinite: false,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
          initialSlide: 0,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  useEffect(() => {
    if (sliderRef.current) sliderRef.current.slickGoTo(currentSlide);
  }, [currentSlide]);

  return (
    <>
      <Slider {...settings} ref={sliderRef}>
        {items?.map((item: ISliderItem, i: number) => (
          <RecipeCard key={i} item={item} />
        ))}
      </Slider>
    </>
  );
}
